/* import __COLOCATED_TEMPLATE__ from './error-banner.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import type ApiErrorSummary from 'embercom/models/standalone/api-error-summary';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'embercom/services/intl';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';

const HIDE_IN_ROUTES = [
  'apps.app.loading',
  'apps.app.copilot-frame',
  'apps.app.settings.standalone.error-logs',
];

export const API_ERROR_BANNER_KEY = 'fin-api-error-banner';

export interface Args {}

export default class StandaloneErrorBanner extends Component<Args> {
  @service declare appService: {
    mainStandalonePlatform: {
      supportsErrorLogs: boolean;
    };
    app: {
      id: string;
      isStandaloneApp: boolean;
      hasStandalonePlatform: (platform: string) => boolean;
    };
  };
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @trackedInLocalStorage({ keyName: API_ERROR_BANNER_KEY }) bannerDismissedAt: number | undefined =
    undefined;

  dataLoader = DeduplicatedAsyncData(
    this,
    () => [this.app.id],
    async (appId) => {
      return (
        this.store.peekRecord('standalone/api-error-summary', appId) ||
        this.store.findRecord('standalone/api-error-summary', appId)
      );
    },
  );

  get app() {
    return this.appService.app;
  }

  get errorSummary(): ApiErrorSummary | undefined {
    return this.dataLoader.value;
  }

  get shouldShowBanner(): boolean {
    if (!this.app.isStandaloneApp || !this.appService.mainStandalonePlatform.supportsErrorLogs) {
      return false;
    }

    if (HIDE_IN_ROUTES.includes(this.router.currentRouteName)) {
      return false;
    }

    return (
      (this.errorSummary?.hasRecentError &&
        (!this.bannerDismissedAt || this.lastErrorAfterDismissal)) ||
      false
    );
  }

  get formattedLastErrorAt(): string {
    let dateTime = this.intl.formatDate(this.errorSummary!.lastErrorAt, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    });

    let timezoneParts = new Intl.DateTimeFormat('en', {
      timeZoneName: 'short',
    }).formatToParts(this.errorSummary!.lastErrorAt);

    let timezone = timezoneParts.find((part) => part.type === 'timeZoneName')?.value || '';

    return `${dateTime} (${timezone})`;
  }

  get lastErrorAfterDismissal(): boolean {
    if (!this.errorSummary?.lastErrorAt || !this.bannerDismissedAt) {
      return false;
    }

    return this.errorSummary.lastErrorAt.getTime() > this.bannerDismissedAt;
  }

  @action
  hideBanner() {
    this.bannerDismissedAt = Date.now();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Common::ErrorBanner': typeof StandaloneErrorBanner;
    'standalone/common/error-banner': typeof StandaloneErrorBanner;
  }
}
